import { ACCOUNT_FEATURE } from '@socialive/common';
import { useFeature } from '@socialive/features';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import downloadRecording from '~/helpers/downloadRecording';
import { FileExportFormat, FileExportState } from '~/helpers/enums';
import { loadConversionStatus, requestConversion } from '~/store/recordings';
import convertTimeEstimateToText from './convertTimeEstimateToText';
import DownloadRequestModal from './DownloadRequestModal';

const conversionLoadingSpinner = (
  <div>
    <div className="spinner relative-spinner" />
    <span className="loadingText">Loading...</span>
  </div>
);

export default function useDownloadConversion({ id, namingConvention, durationSeconds, durationMillis, pagePath }) {
  const dispatch = useDispatch();
  // If we do not want to filter types via the config then this can be removed and the filter that uses the value
  // also the config can be updated to not have the values and the relevant selectors removed.
  const [, { types }] = useFeature(ACCOUNT_FEATURE.EXPORT_VIDEO);

  const [downloadRequestedModal, setDownloadRequestedModal] = useState(null);
  const [conversionOptions, setConversionOptions] = useState([
    {
      label: conversionLoadingSpinner,
    },
  ]);
  const sourceType = useMemo(() => (namingConvention === 'clips' ? 'clip' : 'stream'), [namingConvention]);
  const videoDurationInMinutes = useMemo(
    () => (durationMillis ? durationMillis / (60 * 1000) : durationSeconds / 60),
    [durationMillis, durationSeconds]
  );

  const onRequestConversion = useCallback(
    async (format) => {
      dispatch(requestConversion({ id, format, sourceType, pagePath }));
      // if we have a payload returned then the API request was successful
      const timeEstimateText = convertTimeEstimateToText(+(types[format]?.ratio ?? 0) * +videoDurationInMinutes);
      setDownloadRequestedModal(
        <DownloadRequestModal
          timeEstimateText={timeEstimateText}
          setDownloadRequestedModal={setDownloadRequestedModal}
        />
      );
    },
    [types, videoDurationInMinutes, id, sourceType, dispatch, pagePath]
  );

  const onLoadConversionStatus = useCallback(async () => {
    setConversionOptions([{ label: conversionLoadingSpinner }]);
    const { payload } = await dispatch(loadConversionStatus({ sourceType, id }));
    const { conversions } = payload ?? {};

    const menuConversionOptions = conversions
      ?.filter(({ type }) => types[type?.format]?.enabled)
      ?.filter(({ type, state }) => {
        if (type?.format === FileExportFormat.SRT) return state === FileExportState.COMPLETED;
        if (type?.format === FileExportFormat.TRANSCRIPT) return state === FileExportState.COMPLETED;
        return true;
      })
      ?.map((conversion) => ({ ...conversion, order: types[conversion?.type?.format].order }))
      ?.sort((a, b) => a.order - b.order)
      ?.map(({ type, state, downloadUrl, displayText }) => {
        const { value, format } = type;
        switch (true) {
          case format === FileExportFormat.VTT && state === FileExportState.NOT_STARTED:
            return {
              label: `Export captions and transcript`,
              onClick: () => onRequestConversion(FileExportFormat.CAPTIONS),
            };
          case format === FileExportFormat.MP4 && state === FileExportState.NOT_STARTED:
            return {
              label: `Export CFR video for editing (mp4)`,
              onClick: () => onRequestConversion(FileExportFormat.MP4),
            };
          case format === FileExportFormat.MP3 && state === FileExportState.NOT_STARTED:
            return { label: `Export audio (mp3)`, onClick: () => onRequestConversion(FileExportFormat.MP3) };
          case format === FileExportFormat.VTT && state === FileExportState.IN_PROGRESS:
            return { label: `Generating captions and transcript`, style: { color: 'grey' } };
          case format === FileExportFormat.VTT && state === FileExportState.COMPLETED:
            return { label: `Download formatted caption file (vtt)`, onClick: () => downloadRecording(downloadUrl) };
          case format === FileExportFormat.SRT && state === FileExportState.COMPLETED:
            return { label: `Download simple caption file (srt)`, onClick: () => downloadRecording(downloadUrl) };
          case format === FileExportFormat.TRANSCRIPT && state === FileExportState.COMPLETED:
            return { label: `Download transcript file (txt)`, onClick: () => downloadRecording(downloadUrl) };
          case format === FileExportFormat.MP3 && state === FileExportState.IN_PROGRESS:
            return { label: `Generating audio (mp3)`, style: { color: 'grey' } };
          case format === FileExportFormat.MP3 && state === FileExportState.COMPLETED:
            return { label: `Download audio (mp3)`, onClick: () => downloadRecording(downloadUrl) };
          case format === FileExportFormat.MP4 && state === FileExportState.IN_PROGRESS:
            return { label: `Generating CFR video for editing (mp4)`, style: { color: 'grey' } };
          case format === FileExportFormat.MP4 && state === FileExportState.COMPLETED:
            return { label: `Download CFR video for editing (mp4)`, onClick: () => downloadRecording(downloadUrl) };
          case state === FileExportState.IN_PROGRESS:
            return { label: `Generating ${value}`, style: { color: 'grey' } };
          case state === FileExportState.COMPLETED:
            return { label: `Download ${value}`, onClick: () => downloadRecording(downloadUrl) };
          case state === FileExportState.FAILED:
            return { label: `Retry export to ${value}`, onClick: () => onRequestConversion(format) };
          default:
            return { label: `Export to ${value}`, onClick: () => onRequestConversion(format) };
        }
      });

    setConversionOptions(menuConversionOptions ?? [{ label: 'Exports currently unavailable' }]);
  }, [dispatch, types, onRequestConversion, id, sourceType]);

  return {
    onLoadConversionStatus,
    conversionOptions,
    downloadRequestedModal,
  };
}
